import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import ExitIntentPopup from '../components/ExitIntentPopup';
import { Button } from '../components/ui/button';
import { 
  Code, 
  Copy, 
  Check,
  Play,
  FileCode,
  Globe,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const Integration = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const [copied, setCopied] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [settings, setSettings] = useState(null);
  const [activeTab, setActiveTab] = useState('script');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const embedCode = `<!-- HICALL Widget -->
<script>
(function() {
  var s = document.createElement('script');
  s.src = '${API}/api/widget.js';
  s.async = true;
  s.setAttribute('data-user-id', '${user?.id}');
  s.setAttribute('data-language', 'fr');
  document.body.appendChild(s);
})();
</script>`;

  const iframeCode = `<!-- HICALL Widget (iframe) -->
<iframe 
  src="${API}/widget/${user?.id}" 
  style="display:none;" 
  id="hicall-widget"
></iframe>`;

  const handleCopy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success(t('copied'));
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#0a0a0f]' : 'bg-gray-50'}`} style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Navbar />
      
      {/* Background effects */}
      {isDark && (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#3272F1]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-600/5 rounded-full blur-3xl"></div>
        </div>
      )}
      
      <main className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className={`text-3xl font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('integrationTitle')}
          </h1>
          <p className={`mt-2 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
            {t('integrationDesc')}
          </p>
        </div>

        {/* Demo Section */}
        <div className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-sm'} rounded-2xl border p-6 mb-8`}>
          <div className="flex items-start justify-between">
            <div>
              <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                <Play className="w-5 h-5 text-[#3272F1]" />
                {t('demoTitle')}
              </h2>
              <p className={`text-sm mt-1 ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>{t('demoDesc')}</p>
            </div>
            <Button 
              onClick={() => setShowDemo(true)}
              className="bg-[#3272F1] hover:bg-[#2560d9] gap-2"
              data-testid="show-demo-btn"
            >
              <Play className="w-4 h-4" />
              {language === 'fr' ? 'Voir le popup' : 'View popup'}
            </Button>
          </div>
        </div>

        {/* Integration Code */}
        <div className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-sm'} rounded-2xl border overflow-hidden`}>
          <div className={`p-6 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              <Code className="w-5 h-5 text-[#3272F1]" />
              {language === 'fr' ? 'Code d\'intégration' : 'Integration code'}
            </h2>
          </div>
          
          {/* Tabs */}
          <div className={`flex border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
            <button
              onClick={() => setActiveTab('script')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'script'
                  ? 'text-[#3272F1] border-b-2 border-[#3272F1] bg-[#3272F1]/5'
                  : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileCode className="w-4 h-4" />
              Script JS
            </button>
            <button
              onClick={() => setActiveTab('iframe')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 text-sm font-medium transition-colors ${
                activeTab === 'iframe'
                  ? 'text-[#3272F1] border-b-2 border-[#3272F1] bg-[#3272F1]/5'
                  : isDark ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Globe className="w-4 h-4" />
              iFrame
            </button>
          </div>

          <div className="p-6">
            {/* Script Tab */}
            {activeTab === 'script' && (
              <div className="space-y-4">
                <div className="relative">
                  <pre className={`${isDark ? 'bg-gray-950 text-gray-300 border-gray-800' : 'bg-gray-100 text-gray-800 border-gray-200'} p-4 rounded-xl overflow-x-auto text-sm border`}>
                    <code>{embedCode}</code>
                  </pre>
                  <Button
                    size="sm"
                    className={`absolute top-3 right-3 gap-2 ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'}`}
                    onClick={() => handleCopy(embedCode)}
                    data-testid="copy-script-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? t('copied') : t('copyCode')}
                  </Button>
                </div>
                
                <div className={`${isDark ? 'bg-[#3272F1]/10 border-[#3272F1]/20' : 'bg-blue-50 border-blue-200'} border rounded-xl p-4`}>
                  <h4 className="font-semibold text-[#3272F1] mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4" />
                    Instructions
                  </h4>
                  <ol className={`list-decimal list-inside space-y-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    <li>{language === 'fr' ? 'Copiez le code ci-dessus' : 'Copy the code above'}</li>
                    <li>
                      {language === 'fr' 
                        ? 'Collez-le juste avant la balise ' 
                        : 'Paste it just before the '}
                      <code className={`${isDark ? 'bg-gray-800' : 'bg-gray-200'} px-2 py-0.5 rounded text-[#3272F1]`}>&lt;/body&gt;</code>
                      {language === 'fr' ? ' de votre site' : ' tag of your site'}
                    </li>
                    <li>{language === 'fr' ? 'Le popup s\'affichera automatiquement lors de l\'exit intent' : 'The popup will display automatically on exit intent'}</li>
                  </ol>
                </div>
              </div>
            )}

            {/* iFrame Tab */}
            {activeTab === 'iframe' && (
              <div className="space-y-4">
                <div className="relative">
                  <pre className={`${isDark ? 'bg-gray-950 text-gray-300 border-gray-800' : 'bg-gray-100 text-gray-800 border-gray-200'} p-4 rounded-xl overflow-x-auto text-sm border`}>
                    <code>{iframeCode}</code>
                  </pre>
                  <Button
                    size="sm"
                    className={`absolute top-3 right-3 gap-2 ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' : 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300'}`}
                    onClick={() => handleCopy(iframeCode)}
                    data-testid="copy-iframe-btn"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? t('copied') : t('copyCode')}
                  </Button>
                </div>
              </div>
            )}

            {/* User ID Info */}
            <div className={`mt-6 pt-6 border-t ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>{language === 'fr' ? 'Votre ID utilisateur' : 'Your user ID'}:</p>
                  <p className={`font-mono text-sm ${isDark ? 'bg-gray-800 text-[#3272F1]' : 'bg-gray-100 text-[#3272F1]'} px-4 py-2 rounded-lg mt-2 inline-block`}>{user?.id}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Compatibility */}
        <div className={`${isDark ? 'bg-gradient-to-br from-gray-900 to-gray-950 border-gray-800' : 'bg-white border-gray-200 shadow-sm'} rounded-2xl border p-6 mt-8`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{language === 'fr' ? 'Compatibilité' : 'Compatibility'}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['WordPress', 'Shopify', 'Wix', 'HTML/Custom'].map((platform) => (
              <div 
                key={platform} 
                className={`flex items-center gap-3 ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} rounded-xl px-4 py-3`}
              >
                <div className="w-6 h-6 bg-[#3272F1]/20 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-[#3272F1]" />
                </div>
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{platform}</span>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Demo Popup */}
      {showDemo && settings && (
        <ExitIntentPopup
          userId={user?.id}
          settings={settings}
          language={language}
          onClose={() => setShowDemo(false)}
          forceShow={true}
        />
      )}
    </div>
  );
};

export default Integration;
